"use client";

import { useRouter } from "next/navigation";
import { useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { Box } from "~/components/Box/Box";
import { LocationPanel } from "~/components/LocationPanel/LocationPanel";
import { Modal } from "~/components/Modal/Modal";
import { OptionButton } from "~/components/OptionButton/OptionButton";
import { Stack } from "~/components/Stack/Stack";
import { StoreHours } from "~/components/StoreHours/StoreHours";
import { Text } from "~/components/Text/Text";
import { mapCartRecordToCart, useCart } from "~/hooks/cart";
import { useLocationDeals } from "~/hooks/deals";
import { useIsBetaEnabled } from "~/hooks/features";
import { useHours, useStoreTime } from "~/hooks/hours";
import { useDispatchTypeLabel } from "~/hooks/language";
import {
  useLocation,
  useLocationOrderingEnabled,
  useLocations,
} from "~/hooks/locations";
import { useMenu } from "~/hooks/menu-v2";
import {
  useAllowOrderNotes,
  useAvailableOrderDispatchOptions,
  useOrderNotesMaxLength,
  useTimeSlotsEnabled,
  useWaitTimeForDispatchType,
} from "~/hooks/ordering";
import { useRecentCarts } from "~/hooks/recent-cart";
import type { LocationOrderingEnabled, OrderDispatchOption } from "~/types";
import { cartReorderable, mapCartDetails } from "~/utils/cart";

export default function LocationPage() {
  const { locationRef, location, loading: loadingLocation } = useLocation();
  const { orderingEnabled } = useLocationOrderingEnabled(locationRef);

  const router = useRouter();
  const [isNavigating, setIsNavigating] = useState<boolean>(false);

  const { locations, merchantName, loading: loadingLocations } = useLocations();

  const {
    reorderCart,
    setOrderingOptions,
    timeSlot,
    serviceRef,
    dispatchOption,
  } = useCart(locationRef);

  const allowOrderNotes = useAllowOrderNotes(locationRef, dispatchOption);

  const orderNotesMaxLength = useOrderNotesMaxLength(
    locationRef,
    dispatchOption,
  );

  const { carts, loading: loadingRecentCart } = useRecentCarts(locationRef, 1);
  const recentCartRecord = carts.length ? carts[0] : null;

  const recentCart = recentCartRecord?.items.length
    ? mapCartRecordToCart(recentCartRecord)
    : null;

  const recentCartTimestamp = recentCartRecord?.timestamp;

  const reorderDispatchOption = recentCartRecord
    ? recentCartRecord?.fulfillmentType || "PICK_UP" // Note: default fulfillmentType is set to "PICK_UP" to prevent UI errors for old carts
    : null;

  const { menu: menuV2, isLoading: loadingMenuForReorder } = useMenu(
    reorderDispatchOption,
    null, //timeSlot is not taken into account for a reorder
  );

  const hours = useHours("STORE");

  const { deals, loading: loadingDeals } = useLocationDeals(
    locationRef,
    reorderDispatchOption,
  );

  const reorderDetails = useMemo(
    () => mapCartDetails(recentCart?.items ?? [], menuV2 || [], deals),
    [menuV2, recentCart?.items, deals],
  );

  const reorderLabel = reorderDetails.cartDetails
    .map((item) => item.product?.title)
    .filter(Boolean)
    .join(", ");

  const handlePress = (
    choice: "DELIVERY" | "PICK_UP" | "SERVICE",
    hasTimeSlots = false,
  ) => {
    if (isNavigating) return;

    setIsNavigating(true);

    // If we have a time slot selected, and time slots have been disabled, null it out
    const newTimeSlot = hasTimeSlots ? timeSlot : null;

    setOrderingOptions(choice, newTimeSlot, serviceRef);

    if (choice === "PICK_UP" && hasTimeSlots) {
      router.replace(`/${locationRef}/when`);
      return;
    }

    if (choice === "DELIVERY") {
      router.replace(`/${locationRef}/delivery-address`);
      return;
    }

    router.replace(`/${locationRef}/menu`);
  };

  const handleClose = () => {
    if (dispatchOption)
      setOrderingOptions(dispatchOption, timeSlot, serviceRef);

    router.replace(`/${locationRef}/menu`);
  };

  const handlePressReorder = () => {
    if (!recentCartRecord) return;
    if (!recentCart?.items.length) return;
    if (!reorderDispatchOption) return;

    const notes =
      recentCartRecord.notes?.substring(0, orderNotesMaxLength) || "";

    reorderCart(
      recentCartRecord.locationRef,
      recentCart,
      reorderDispatchOption,
      allowOrderNotes ? notes : "",
    );

    router.push(`/${recentCartRecord.locationRef}/cart`);
  };

  const title =
    merchantName && location ? `${merchantName} ${locationRef}` : "";

  const skeleton = hours?.loading || loadingLocation;

  const { setEnabled: setBetaEnabled } = useIsBetaEnabled();

  if (locationRef === "test" || locationRef === "jets") {
    setBetaEnabled();
  }

  return (
    <Modal title={title} open={true} onClose={handleClose}>
      <Stack space={"5"}>
        <LocationPanel
          canChangeLocation={!loadingLocations && (locations?.length || 0) > 1}
          loc={location}
          skeleton={loadingLocation}
        />
        <StoreHours type="STORE" skeleton={skeleton} hours={hours} />
        <AvailableOptionsPanel
          onPress={handlePress}
          loc={orderingEnabled}
          locationRef={locationRef}
        />
        {(loadingRecentCart ||
          loadingDeals ||
          cartReorderable(reorderDetails)) && (
          <ReOrderPanel
            title={reorderLabel}
            timestamp={recentCartTimestamp}
            loading={loadingRecentCart || loadingMenuForReorder || loadingDeals}
            onPress={handlePressReorder}
          />
        )}
      </Stack>
    </Modal>
  );
}

function AvailableOptionsPanel(props: {
  locationRef: string;
  loc?: LocationOrderingEnabled;
  onPress: (option: OrderDispatchOption, hasTimeSlots?: boolean) => void;
}) {
  const { loc, locationRef, onPress } = props;

  return (
    <Stack space="3">
      {loc?.orderAheadEnabled && (
        <PickUpButton
          onPress={(hasTimeSlots) => onPress("PICK_UP", hasTimeSlots)}
          locationRef={locationRef}
        />
      )}
      {loc?.orderAtTableEnabled && (
        <ServiceButton
          onPress={() => onPress("SERVICE")}
          locationRef={locationRef}
        />
      )}
      {loc?.orderDeliveryEnabled && (
        <DeliveryButton
          onPress={(hasTimeSlots) => onPress("DELIVERY", hasTimeSlots)}
          locationRef={locationRef}
        />
      )}
    </Stack>
  );
}

function ReOrderPanel(props: {
  loading: boolean;
  timestamp?: string;
  title?: string;
  onPress: () => void;
}) {
  const { formatDateTimeWithZone } = useStoreTime();

  return (
    <>
      <Box paddingBottom={"3"} flexGrow={1}>
        <Text size="standard" weight="semi-strong">
          {props.loading ? <Skeleton width={120} /> : "Quickly reorder"}
        </Text>
      </Box>
      <OptionButton
        title={props.title}
        loading={props.loading}
        subTitle={
          props.timestamp ? (
            <>
              Last ordered:{" "}
              <time dateTime={new Date(props.timestamp).toISOString()}>
                {formatDateTimeWithZone(props.timestamp)}
              </time>
            </>
          ) : (
            <>...</>
          )
        }
        onPress={props.onPress}
        icon={"bolt"}
        iconColor={"var(--color-accent)"}
      />
    </>
  );
}

function DeliveryButton(props: {
  onPress: (hasDeliveryTimeSlots: boolean) => void;
  locationRef: string;
}) {
  const hours = useHours("DELIVERY");
  const { label } = useDispatchTypeLabel("DELIVERY");
  const timeSlotsEnabled = useTimeSlotsEnabled(props.locationRef, "DELIVERY");

  const waitTime = useWaitTimeForDispatchType(props.locationRef, "DELIVERY");

  const {
    availableOrderDispatchOptions,
    loadingAvailableOrderDispatchOptions: loading,
  } = useAvailableOrderDispatchOptions(props.locationRef);

  if (!loading && !availableOrderDispatchOptions.includes("DELIVERY"))
    return null;

  let subTitle = "";
  if (hours?.status === "closed") {
    subTitle = timeSlotsEnabled ? "Schedule for Later" : "Ordering Unavailable";
  } else if (waitTime) {
    subTitle = timeSlotsEnabled ? `${waitTime}, or for later` : waitTime;
  }

  const handlePress = () => {
    props.onPress(timeSlotsEnabled);
  };

  return (
    <OptionButton
      onPress={handlePress}
      title={label || ""}
      subTitle={subTitle}
      icon="delivery"
      loading={loading}
    />
  );
}

function PickUpButton(props: {
  onPress: (hasTimeSlots: boolean) => void;
  locationRef: string;
}) {
  const hours = useHours("PICK_UP");
  const { label } = useDispatchTypeLabel("PICK_UP");
  const timeSlotsEnabled = useTimeSlotsEnabled(props.locationRef, "PICK_UP");

  const {
    availableOrderDispatchOptions,
    loadingAvailableOrderDispatchOptions: loading,
  } = useAvailableOrderDispatchOptions(props.locationRef);

  const waitTime = useWaitTimeForDispatchType(props.locationRef, "PICK_UP");

  if (!loading && !availableOrderDispatchOptions.includes("PICK_UP"))
    return null;

  const handlePress = () => {
    props.onPress(timeSlotsEnabled);
  };

  const subTitle = formatPickUpSubTitle(
    hours?.status === "closed",
    waitTime,
    timeSlotsEnabled,
  );

  return (
    <OptionButton
      onPress={handlePress}
      title={label || ""}
      subTitle={subTitle}
      icon="pick-up"
      loading={loading}
    />
  );
}

function formatPickUpSubTitle(
  closed: boolean,
  waitTime: string | null,
  hasTimeSlots: boolean,
) {
  if (closed)
    return hasTimeSlots ? "Schedule for later" : "Ordering Unavailable";

  if (waitTime) {
    return hasTimeSlots
      ? `ASAP - ${waitTime}, or for later`
      : `ASAP - ${waitTime}`;
  }

  return "ASAP or Schedule for later";
}

function ServiceButton(props: { onPress: () => void; locationRef: string }) {
  const { label } = useDispatchTypeLabel("SERVICE");
  const hours = useHours("SERVICE");
  const timeSlotsEnabled = useTimeSlotsEnabled(props.locationRef, "PICK_UP");
  const waitTime = useWaitTimeForDispatchType(props.locationRef, "SERVICE");
  const {
    availableOrderDispatchOptions,
    loadingAvailableOrderDispatchOptions: loading,
  } = useAvailableOrderDispatchOptions(props.locationRef);

  if (!loading && !availableOrderDispatchOptions.includes("SERVICE"))
    return null;

  let subTitle = "";
  if (hours?.status === "closed") {
    subTitle = timeSlotsEnabled ? "Schedule for Later" : "Ordering Unavailable";
  } else if (waitTime) {
    subTitle = timeSlotsEnabled ? `${waitTime}, or for later` : waitTime;
  }

  return (
    <OptionButton
      onPress={props.onPress}
      title={label || ""}
      subTitle={subTitle}
      icon="service"
      loading={loading}
    />
  );
}
